import React, { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardText, UploadSimple, X } from "@phosphor-icons/react";
import { http } from "@/lib/api";

// Mirrors backend consensus.normalize_name exactly.
const normKey = (n) => String(n || "").toLowerCase().trim().replace(/[.'`]/g, "").replace(/\s+(jr|sr|ii|iii|iv|v)\b\.?/g, "").replace(/\s+/g, " ").trim();

const NAME_ALIASES = new Set(["player", "name", "player_name", "playername", "player name", "pname", "dk name", "nickname"]);
const PROJ_ALIASES = new Set(["proj", "projection", "projections", "points", "fpts", "median", "proj_pts", "proj pts", "fpts_proj", "projpoints", "projected points"]);
const OWN_ALIASES = new Set(["own", "own%", "ownership", "ownership%", "pown", "pown%", "projown", "proj_own", "proj own", "projected own", "projected_ownership", "own_pct", "own%proj", "projownership", "projectedownership", "roster%", "roster percent"]);

const norm = (s) => String(s ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");
const normSet = (set) => new Set([...set].map(norm));
const NAME_N = normSet(NAME_ALIASES);
const PROJ_N = normSet(PROJ_ALIASES);
const OWN_N = normSet(OWN_ALIASES);
// Showdown exports split per slot (DFS Army: ProjOwnCPT / ProjOwnFLEX).
const SLOT_SUFFIX = { cpt: "cpt", captain: "cpt", mvp: "cpt", cptn: "cpt", flex: "flex", flx: "flex", util: "flex", utility: "flex" };

// → { kind: "name"|"proj"|"own"|null, slot: ""|"cpt"|"flex" }
const matchHeader = (header) => {
  const key = norm(header);
  if (NAME_N.has(key)) return { kind: "name", slot: "" };
  if (PROJ_N.has(key)) return { kind: "proj", slot: "" };
  if (OWN_N.has(key)) return { kind: "own", slot: "" };
  for (const [marker, slot] of Object.entries(SLOT_SUFFIX)) {
    const bases = [];
    if (key.endsWith(marker) && key.length > marker.length) bases.push(key.slice(0, -marker.length));
    if (key.startsWith(marker) && key.length > marker.length) bases.push(key.slice(marker.length));
    for (const base of bases) {
      if (NAME_N.has(base)) return { kind: "name", slot };
      if (PROJ_N.has(base)) return { kind: "proj", slot };
      if (OWN_N.has(base)) return { kind: "own", slot };
    }
  }
  return { kind: null, slot: "" };
};

const splitRow = (line, delim) => {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') { cur += '"'; i += 1; } else quoted = !quoted;
    } else if (ch === delim && !quoted) {
      out.push(cur); cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out.map((c) => c.replace(/^\ufeff/, "").trim());
};

const toNum = (v) => {
  const s = String(v ?? "").replace(/[%$,]/g, "").trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

// Paste or drop any site's export — headers are auto-mapped, then you confirm.
export function parseCompetitorText(text) {
  const lines = String(text || "").split(/\r?\n/).filter((l) => l.trim() !== "");
  if (!lines.length) return { rows: [], cols: null, error: "Nothing to import" };
  const delim = (lines[0].match(/\t/g) || []).length > (lines[0].match(/,/g) || []).length ? "\t" : ",";
  const first = splitRow(lines[0], delim);
  const hits = first.map(matchHeader);
  const hasHeader = hits.some((h) => h.kind === "name" || h.kind === "proj" || h.kind === "own");
  const findIdx = (kind, slot) => hits.findIndex((h) => h.kind === kind && h.slot === slot);
  let iName = 0;
  let iProj = 1;
  let iOwn = -1;
  let iOwnCpt = -1;
  let iOwnFlex = -1;
  let iProjCpt = -1;
  if (hasHeader) {
    iName = findIdx("name", "");
    if (iName < 0) iName = hits.findIndex((h) => h.kind === "name");
    iProj = findIdx("proj", "");
    if (iProj < 0) iProj = findIdx("proj", "flex");
    if (iProj < 0) iProj = findIdx("proj", "cpt");
    iOwn = findIdx("own", "");
    iOwnCpt = findIdx("own", "cpt");
    iOwnFlex = findIdx("own", "flex");
    iProjCpt = findIdx("proj", "cpt");
    if (iProjCpt === iProj) iProjCpt = -1;
  } else {
    iOwn = 2;
  }
  if (iName < 0) return { rows: [], cols: null, error: "No player-name column found — add a Name/Player header" };
  const hasOwn = iOwn >= 0 || iOwnCpt >= 0 || iOwnFlex >= 0;
  if (iProj < 0 && !hasOwn) return { rows: [], cols: null, error: "No projection or ownership column found" };
  // In a showdown export the slot-free ownership column IS the FLEX share
  // (Stokastic ships `Ownership %` + `CPT Ownership %`), so when a CPT column
  // exists the two halves are summed into real lineup ownership.
  const iOwnBase = iOwnFlex >= 0 ? iOwnFlex : iOwn;
  const sumSlots = iOwnCpt >= 0 && iOwnBase >= 0;
  const body = hasHeader ? lines.slice(1) : lines;
  const rows = [];
  for (const line of body) {
    const cells = splitRow(line, delim);
    const name = (cells[iName] || "").trim();
    if (!name) continue;
    const proj = iProj >= 0 ? toNum(cells[iProj]) : null;
    let own = null;
    let ownCpt = null;
    let ownFlex = null;
    if (iOwnCpt >= 0) {
      const cpt = toNum(cells[iOwnCpt]);
      const base = iOwnBase >= 0 ? toNum(cells[iOwnBase]) : null;
      ownCpt = cpt;
      ownFlex = base;
      if (cpt !== null || base !== null) own = Math.round(Math.min(100, (cpt || 0) + (base || 0)) * 10) / 10;
    } else if (iOwnBase >= 0) {
      own = toNum(cells[iOwnBase]);
      if (iOwnFlex >= 0) ownFlex = own;
    }
    const projCpt = iProjCpt >= 0 ? toNum(cells[iProjCpt]) : null;
    if (proj === null && own === null) continue;
    rows.push({ name, proj, own, own_cpt: ownCpt, own_flex: ownFlex, proj_cpt: projCpt });
  }
  const ownLabel = sumSlots
    ? `${first[iOwnBase]} + ${first[iOwnCpt]}`
    : iOwnBase >= 0
      ? (hasHeader ? first[iOwnBase] : "col 3")
      : iOwnCpt >= 0 ? first[iOwnCpt] : null;
  return {
    rows,
    cols: {
      name: hasHeader ? first[iName] : "col 1",
      proj: iProj >= 0 ? (hasHeader ? first[iProj] : "col 2") : null,
      own: ownLabel,
      ownSummed: sumSlots,
    },
    error: rows.length ? null : "No usable rows — every row was missing a number",
  };
}

export default function CsvPasteImport({ tab, pool, onImported, dg }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const parsed = useMemo(() => (text.trim() ? parseCompetitorText(text) : null), [text]);
  const poolKeys = useMemo(() => new Set((pool || []).map((p) => normKey(p.name))), [pool]);
  const matched = useMemo(
    () => (parsed?.rows || []).filter((r) => poolKeys.has(normKey(r.name))),
    [parsed, poolKeys],
  );
  const unmatched = useMemo(
    () => (parsed?.rows || []).filter((r) => !poolKeys.has(normKey(r.name))),
    [parsed, poolKeys],
  );
  const withOwn = useMemo(() => (parsed?.rows || []).filter((r) => r.own != null && r.own > 0).length, [parsed]);

  const readFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.onerror = () => toast.error("Could not read that file");
    reader.readAsText(file);
  };

  const doImport = async () => {
    const rows = parsed?.rows || [];
    if (!rows.length) return;
    setBusy(true);
    try {
      const r = await http.put(`/competitors/${tab}/players`, {
        entries: rows.map((x) => ({
          name: x.name, proj: x.proj, own: x.own,
          own_cpt: x.own_cpt ?? null, own_flex: x.own_flex ?? null, proj_cpt: x.proj_cpt ?? null,
        })),
      }, { params: { dg: dg ?? undefined } });
      toast.success(`Imported ${rows.length} rows into ${tab}`, {
        description: `${matched.length} matched this slate's pool${unmatched.length ? ` · ${unmatched.length} not on the slate (kept for later)` : ""}`,
      });
      setText("");
      setOpen(false);
      onImported?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="cpg-open-paste-import"
        className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-[#FFCC00]/60 text-[#FFCC00] hover:bg-[#FFCC00] hover:text-black font-bold inline-flex items-center gap-1 transition-colors"
      >
        <ClipboardText size={11} weight="fill" /> Paste CSV
      </button>
    );
  }

  return (
    <div
      className={`w-full border p-3 mt-1 ${dragging ? "border-[#39FF14] bg-[#39FF14]/5" : "border-[#FFCC00]/40 bg-black/40"}`}
      data-testid="cpg-paste-import"
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); readFile(e.dataTransfer?.files?.[0]); }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="mono text-[10px] uppercase tracking-widest text-[#FFCC00] font-bold">
          Import into {tab} · paste CSV / TSV or drop the file
        </div>
        <button type="button" onClick={() => { setOpen(false); setText(""); }} data-testid="cpg-paste-close"
          className="text-zinc-500 hover:text-white"><X size={13} weight="bold" /></button>
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        data-testid="cpg-paste-textarea"
        placeholder={"Name,Points,ProjOwn\nJahmyr Gibbs,25.08,18.4\nJaxon Smith-Njigba,26.18,22.1"}
        rows={5}
        className="w-full bg-black border border-zinc-800 focus:border-[#FFCC00] outline-none px-2 py-1.5 mono text-[11px] text-zinc-200 resize-y"
      />
      <div className="flex items-center gap-3 flex-wrap mt-2">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="cpg-paste-pick-file"
          className="mono text-[10px] uppercase tracking-widest px-2 py-1 border border-zinc-700 text-zinc-300 hover:border-[#FFCC00] inline-flex items-center gap-1">
          <UploadSimple size={11} /> Choose file
        </button>
        <input ref={fileRef} type="file" accept=".csv,.tsv,.txt" className="hidden"
          onChange={(e) => readFile(e.target.files?.[0])} data-testid="cpg-paste-file-input" />
        {parsed?.error && (
          <span className="mono text-[10px] text-[#FF3B30]" data-testid="cpg-paste-error">{parsed.error}</span>
        )}
        {parsed && !parsed.error && (
          <span className="mono text-[10px] text-zinc-400" data-testid="cpg-paste-preview">
            {parsed.rows.length} rows ·{" "}
            <span className="text-[#39FF14]">{matched.length} matched</span>
            {unmatched.length ? <> · <span className="text-[#FF8A00]">{unmatched.length} not on this slate</span></> : null}
            {" · "}{withOwn} with own% · cols: {parsed.cols.name}
            {parsed.cols.proj ? ` / ${parsed.cols.proj}` : ""}{parsed.cols.own ? ` / ${parsed.cols.own}` : ""}
            {parsed.cols.ownSummed ? <span className="text-[#FFCC00]"> (CPT + FLEX summed)</span> : null}
            {!parsed.cols.own ? <span className="text-[#FF8A00]"> · no ownership column found</span> : null}
          </span>
        )}
        <button
          type="button"
          onClick={doImport}
          disabled={busy || !parsed || !!parsed.error}
          data-testid="cpg-paste-confirm"
          className="ml-auto mono text-[10px] uppercase tracking-widest px-3 py-1 border font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14] hover:text-black"
        >
          {busy ? "Importing…" : `Import ${parsed?.rows?.length || 0} rows`}
        </button>
      </div>
      {unmatched.length > 0 && (
        <div className="mono text-[10px] text-zinc-500 mt-2 leading-relaxed" data-testid="cpg-paste-unmatched">
          Not on this slate: {unmatched.slice(0, 12).map((r) => r.name).join(", ")}
          {unmatched.length > 12 ? ` … +${unmatched.length - 12} more` : ""}
        </div>
      )}
      <div className="mono text-[9px] text-zinc-600 mt-2">
        Existing {tab} rows are kept — pasted players overwrite their own rows only.
      </div>
    </div>
  );
}
